/**
 * Sentry bootstrap for the React frontend.
 *
 * Design notes:
 *  - SDK is dynamically initialised so the app boots fine even when
 *    REACT_APP_SENTRY_DSN_FRONTEND is empty (PREVIEW dev without DSN).
 *  - Tracing & session replay are intentionally DISABLED for launch to
 *    keep bundle size + free-tier event quota under control. Flip
 *    tracesSampleRate to ~0.1 later once volume is known.
 *  - PII scrubbing runs in `beforeSend` AND `beforeBreadcrumb` so emails /
 *    phones / names from form inputs, fetch URLs, and console logs never
 *    leave the browser.
 */
import * as Sentry from '@sentry/react';

const PII_KEY_TOKENS = ['email', 'phone', 'name', 'address', 'password', 'token', 'card', 'cvv', 'ssn', 'dob', 'birth'];
const SENSITIVE_HEADERS = ['authorization', 'cookie', 'x-auth-token', 'set-cookie'];

function scrubDeep(key, val) {
    if (val == null) return val;
    if (Array.isArray(val)) return val.map((v) => scrubDeep(key, v));
    if (typeof val === 'object') {
        const out = {};
        for (const [k, v] of Object.entries(val)) {
            out[k] = scrubDeep(k, v);
        }
        return out;
    }
    const lowered = (key || '').toLowerCase();
    if (PII_KEY_TOKENS.some((tok) => lowered.includes(tok))) return '[REDACTED]';
    return val;
}

function beforeSend(event) {
    try {
        // Drop user identifying fields (Sentry sets ip_address=auto by
        // default in newer SDKs; we kill that too).
        if (event.user) {
            const safe = {};
            if (event.user.id) safe.id = event.user.id;
            event.user = safe;
        }
        // Scrub request body + headers
        if (event.request) {
            if (event.request.data) event.request.data = scrubDeep('body', event.request.data);
            if (event.request.headers) {
                for (const h of Object.keys(event.request.headers)) {
                    if (SENSITIVE_HEADERS.includes(h.toLowerCase())) {
                        event.request.headers[h] = '[REDACTED]';
                    }
                }
            }
            // Strip query string entirely — it may contain emails/tokens from
            // password reset links, magic links, etc.
            if (event.request.query_string) event.request.query_string = '[REDACTED]';
        }
        if (event.extra) event.extra = scrubDeep('extra', event.extra);
        if (event.contexts) event.contexts = scrubDeep('contexts', event.contexts);
    } catch {
        // Never block event submission over a scrubbing bug
    }
    return event;
}

function beforeBreadcrumb(crumb) {
    try {
        if (crumb && crumb.data) {
            crumb.data = scrubDeep('breadcrumb', crumb.data);
        }
        // Strip query strings from fetch/xhr breadcrumb URLs
        if (crumb && (crumb.category === 'fetch' || crumb.category === 'xhr') && crumb.data?.url) {
            try {
                const u = new URL(crumb.data.url, window.location.origin);
                u.search = '';
                crumb.data.url = u.toString();
            } catch {
                // Non-URL strings — leave alone
            }
        }
    } catch {
        // No-op
    }
    return crumb;
}

export function initSentry() {
    const dsn = process.env.REACT_APP_SENTRY_DSN_FRONTEND;
    if (!dsn) {
        // Silent no-op when DSN is unset — keeps PREVIEW dev workflow simple
        // and lets us deploy code before DSNs are provisioned.
        return;
    }
    Sentry.init({
        dsn,
        environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || 'preview',
        release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
        // Disable tracing + replay for launch (cost / bundle control)
        tracesSampleRate: 0,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,
        // Don't auto-capture console.log/info as breadcrumbs — only warn+
        // (defaults: log/info/warn/error → we keep warn/error)
        // sendDefaultPii is OFF by default for JS SDK — explicit:
        sendDefaultPii: false,
        beforeSend,
        beforeBreadcrumb,
    });
}

// Re-export Sentry namespace so callers can do `import { captureException } from '@/lib/sentry'`
export { Sentry };
export const captureException = (err, opts) => {
    if (!process.env.REACT_APP_SENTRY_DSN_FRONTEND) return;
    try {
        Sentry.captureException(err, opts);
    } catch {
        // Swallow — never break the app over an observability call
    }
};
